import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Icon } from '@sakhtar/design-system/react';

const fa = (n: number) => n.toLocaleString('fa-IR');
const pad2 = (n: number) => String(n).padStart(2, '0');
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export interface TimeOfDayInputProps {
  /** `''` or `HH:mm` (24h, zero-padded) — same shape a native `type="time"` input produces. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  style?: CSSProperties;
}

function splitTime(value: string): { h: number | null; m: number | null } {
  if (!value) return { h: null, m: null };
  const [h, m] = value.split(':').map(Number);
  return { h: Number.isFinite(h) ? h! : null, m: Number.isFinite(m) ? m! : null };
}

/**
 * Persian-styled time-of-day trigger + popup — same visual language as
 * `JalaliDateInput` (sk-input trigger button, elevated popover card,
 * `--sk-z-overlay` tier, click-outside/Escape to close), replacing a bare
 * native `<input type="time">`. QA finding #8: the native control renders
 * its own untranslatable placeholder glyph (`--:--`) with no way to swap
 * in Persian text, so this is a real custom control, not a styled native
 * input — hour/minute selection only, no calendar system involved (task
 * §3: clock time needs no Jalali/Gregorian conversion).
 */
export function TimeOfDayInput({ value, onChange, disabled, style }: TimeOfDayInputProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { h, m } = splitTime(value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (containerRef.current && target && containerRef.current.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function setHour(nextH: number) {
    onChange(`${pad2(nextH)}:${pad2(m ?? 0)}`);
  }
  function setMinute(nextM: number) {
    onChange(`${pad2(h ?? 0)}:${pad2(nextM)}`);
  }

  // Converting each padded digit individually (not `toLocaleString` on the
  // hour/minute numbers) keeps a leading zero — `fa(0)` alone drops it.
  const displayFa =
    h !== null && m !== null
      ? `${pad2(h)}:${pad2(m)}`.replace(/\d/g, (d) => fa(Number(d)))
      : 'انتخاب ساعت';

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        className="sk-input"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          width: '100%',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: h !== null ? 'var(--sk-fg)' : 'var(--sk-fg-subtle)',
        }}
      >
        <span className="sk-num" dir="ltr">
          {displayFa}
        </span>
        <Icon name="clock" size="sm" />
      </button>

      {open ? (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            insetInlineStart: 0,
            width: 160,
            background: 'var(--sk-bg-elev)',
            border: '1px solid var(--sk-border)',
            borderRadius: 'var(--sk-r-lg)',
            boxShadow: 'var(--sk-shadow-3)',
            zIndex: 'var(--sk-z-overlay)',
            padding: 12,
            display: 'flex',
            gap: 8,
          }}
        >
          <select
            className="sk-select"
            aria-label="ساعت"
            value={h ?? ''}
            onChange={(e) => setHour(Number(e.target.value))}
            style={{ flex: 1 }}
          >
            {h === null ? <option value="">ساعت</option> : null}
            {HOURS.map((hh) => (
              <option key={hh} value={hh}>
                {fa(hh)}
              </option>
            ))}
          </select>
          <select
            className="sk-select"
            aria-label="دقیقه"
            value={m ?? ''}
            onChange={(e) => setMinute(Number(e.target.value))}
            style={{ flex: 1 }}
          >
            {m === null ? <option value="">دقیقه</option> : null}
            {MINUTES.map((mm) => (
              <option key={mm} value={mm}>
                {fa(mm)}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  );
}
